import { getThreadPropertyKey, isAmbiguousStreetAnchor, threadSubjectMatchesRowAnchor } from './threadGrouping';

const SENDABLE_TOUR_ACTION_REASONS = new Set([
  'tour_requested',
  'tour_reschedule_requested',
  'tour_slot_declined',
  'tour_unavailable'
]);

const TOUR_ACTION_LABELS = {
  tour_requested: 'Tour Requested',
  tour_reschedule_requested: 'Tour Reschedule',
  tour_slot_declined: 'Tour Slot Declined',
  tour_unavailable: 'Tour Unavailable'
};

const TERMINAL_ACTION_NOTIFICATION_STATUSES = new Set([
  'dismissed',
  'resolved',
  'cancelled',
  'canceled'
]);

export function isSendableTourActionReason(reason = '') {
  return SENDABLE_TOUR_ACTION_REASONS.has(String(reason || '').trim());
}

export function getTourActionLabel(reason = '') {
  return TOUR_ACTION_LABELS[String(reason || '').trim()] || null;
}

export function hasSuggestedEmailRecipient(notification) {
  const to = notification?.meta?.suggestedEmail?.to;
  if (Array.isArray(to)) return to.some(email => String(email || '').trim());
  return Boolean(String(to || '').trim());
}

export function isSendableActionNotification(notification) {
  if (notification?.kind !== 'action_needed') return false;
  const status = String(notification?.status || '').toLowerCase().trim();
  if (TERMINAL_ACTION_NOTIFICATION_STATUSES.has(status)) return false;

  const reason = notification.meta?.reason || '';
  if (!reason || reason === 'new_property_pending_send') return false;
  if (reason === 'new_property_pending_approval') return true;
  if (isSendableTourActionReason(reason) || reason === 'call_requested') return true;
  if (reason === 'reply_review_required') return true;
  if (reason.startsWith('needs_user_input:')) return true;
  if (reason.startsWith('wrong_contact:')) {
    return hasSuggestedEmailRecipient(notification) ||
      Boolean(String(notification?.meta?.suggestedEmail?.body || '').trim());
  }

  return false;
}

// An opt-out is a NOTICE, never a reply. The broker asked to be removed, so
// the backend stops the conversation and writes this card - and the same stop
// used to hide it, leaving the advisor with nothing at all: no button on the
// campaign row, no header count, no Mark Handled. It is deliberately NOT
// sendable: every send path (getSendableActionNotifications, the panel's
// isActionableNotification, and through it the inline composer) keeps
// rejecting it, so no control that could email an opted-out contact can ever
// bind to it. It is admitted only as current dashboard work.
export function isOptOutNotice(notification) {
  if (notification?.kind !== 'action_needed') return false;
  const status = String(notification?.status || '').toLowerCase().trim();
  if (TERMINAL_ACTION_NOTIFICATION_STATUSES.has(status)) return false;
  return String(notification?.meta?.reason || '').startsWith('contact_optout:');
}

export function hasDashboardActionAnchor(notification) {
  const meta = notification?.meta || {};
  return Boolean(
    notification?.threadId ||
    notification?.rowNumber != null ||
    notification?.rowAnchor ||
    meta.threadId ||
    meta.rowNumber != null ||
    meta.rowAnchor ||
    meta.propertyAddress ||
    meta.address ||
    meta.property?.address
  );
}

export function isCampaignLevelActionNotification(notification) {
  return notification?.kind === 'action_needed' &&
    String(notification?.meta?.reason || '').startsWith('needs_user_input:') &&
    !notification?.threadId && !notification?.meta?.threadId;
}

export function isCurrentDashboardActionNotification(notification) {
  const isUnmatchedMessage = notification?.meta?.reason ===
    'needs_user_input:an_inbound_message_matched_no_conversation';
  return (isSendableActionNotification(notification) || isOptOutNotice(notification)) &&
    (hasDashboardActionAnchor(notification) || isUnmatchedMessage);
}

function getNotificationThreadIds(notification) {
  const meta = notification?.meta || {};
  return [
    notification?.threadId,
    meta.threadId
  ].filter(Boolean);
}

function getNotificationPropertyAnchors(notification) {
  const meta = notification?.meta || {};
  return [
    notification?.rowAnchor,
    meta.rowAnchor,
    meta.propertyAddress,
    meta.address,
    meta.property?.address
  ].filter(Boolean);
}

function isNewPropertyApproval(notification) {
  return (notification?.meta?.reason || '') === 'new_property_pending_approval';
}

const TERMINAL_DASHBOARD_THREAD_STATUSES = new Set([
  'stopped',
  'completed',
  'closed',
  'cancelled',
  'canceled',
  'archived'
]);

function isTerminalDashboardThreadStatus(status) {
  return TERMINAL_DASHBOARD_THREAD_STATUSES.has(String(status || '').toLowerCase().trim());
}

function lookupPropertyMatchesAnchor(propertyKey, anchor) {
  // Lookup values may already be keys with a property name as the last part.
  // Compare that key before treating it as a full subject with a trailing city.
  return Boolean(propertyKey) && (
    String(propertyKey).toLowerCase().trim() === getThreadPropertyKey(anchor).toLowerCase() ||
    threadSubjectMatchesRowAnchor(propertyKey, anchor)
  );
}

// All dashboard projections use the same binding before sorting or deduping.
// A null order leaves the notification unbound; zero is a valid first group.
export function getDashboardNotificationDisplayOrder(notification, threadOrderLookup = {}) {
  const lookupEntries = Object.entries(threadOrderLookup);
  if (lookupEntries.length === 0) return null;

  const threadIds = getNotificationThreadIds(notification);
  // Match the panel's anchor precedence: broad secondary metadata must not
  // override (or rescue a contradiction in) the primary row anchor.
  const anchors = getNotificationPropertyAnchors(notification).slice(0, 1);

  for (const threadId of getNotificationThreadIds(notification)) {
    if (threadOrderLookup[threadId] === undefined) continue;

    if (isNewPropertyApproval(notification)) return threadOrderLookup[threadId];

    const status = String(threadOrderLookup[`threadStatus:${threadId}`] || '').toLowerCase().trim();
    const statusReason = String(threadOrderLookup[`threadStatusReason:${threadId}`] || '')
      .toLowerCase().trim();
    const isStoppedInput = status === 'stopped' &&
      String(notification?.meta?.reason || '').startsWith('needs_user_input:');
    // Stopping a campaign stops every one of its conversations
    // (statusReason 'terminal_stop'). Its still-open cards are work the
    // advisor has not dealt with yet, so they stay listed and counted until
    // they are handled. A conversation the advisor stopped on its own
    // ('user_requested') keeps hiding its stale cards.
    const isCampaignStopped = status === 'stopped' && statusReason === 'terminal_stop';
    // An opt-out always stops the very conversation it arrived on, so this
    // veto used to swallow every opt-out notice ever written. The notice is
    // the advisor's own open work on that stopped conversation: keep it until
    // she handles it (or the campaign stop dismisses the card outright).
    const isStoppedOptOut = status === 'stopped' && isOptOutNotice(notification);
    if (isTerminalDashboardThreadStatus(status) && !isStoppedInput && !isCampaignStopped &&
        !isStoppedOptOut) {
      return null;
    }

    if (anchors.length === 0) return threadOrderLookup[threadId];

    const threadProperty = threadOrderLookup[`threadProp:${threadId}`];
    if (!threadProperty) return threadOrderLookup[threadId];

    return anchors.some(anchor => lookupPropertyMatchesAnchor(threadProperty, anchor))
      ? threadOrderLookup[threadId] : null;
  }

  const rowNumber = notification?.rowNumber ?? notification?.meta?.rowNumber;
  // Preserve the legacy orphan guard only when no row fallback is available.
  if (threadIds.length > 0 && rowNumber == null) return null;

  const propertyEntries = lookupEntries.filter(([key]) => key.startsWith('prop:'));
  const threadProperties = lookupEntries.filter(([key]) => key.startsWith('threadProp:'))
    .map(([, property]) => property);
  const subjects = threadProperties.length ? threadProperties : propertyEntries.map(([key]) => key.slice(5));
  if (anchors.some(anchor => isAmbiguousStreetAnchor(anchor, subjects))) return null;

  const rowEntries = lookupEntries.filter(([key, row]) => key.startsWith('threadRow:') && row != null);
  if (rowNumber != null && rowEntries.length > 0) {
    const orders = new Set(rowEntries.filter(([, row]) => String(row) === String(rowNumber))
      .map(([key]) => key.slice('threadRow:'.length))
      .filter(threadId => !isTerminalDashboardThreadStatus(threadOrderLookup[`threadStatus:${threadId}`]))
      .filter(threadId => anchors.length === 0 || anchors.some(anchor => (
        lookupPropertyMatchesAnchor(threadOrderLookup[`threadProp:${threadId}`], anchor)
      )))
      .map(threadId => threadOrderLookup[threadId])
      .filter(order => order !== undefined));
    return orders.size === 1 ? [...orders][0] : null;
  }

  const matches = propertyEntries.filter(([key]) => anchors.some(anchor => (
    lookupPropertyMatchesAnchor(key.slice(5), anchor)
  )));
  return matches.length === 1 ? matches[0][1] : null;
}

export function matchesDashboardThreadLookup(notification, threadOrderLookup) {
  if (threadOrderLookup === undefined || threadOrderLookup === null) return true;
  return getDashboardNotificationDisplayOrder(notification, threadOrderLookup) !== null;
}

export function isRenderableDashboardActionNotification(notification, threadOrderLookup) {
  if (!isCurrentDashboardActionNotification(notification)) return false;
  if (threadOrderLookup == null) return true;
  const threadIds = getNotificationThreadIds(notification);
  if (threadIds.some(threadId => threadOrderLookup[threadId] !== undefined)) {
    // Keep the known-thread contradictory-anchor and terminal-state vetoes.
    return matchesDashboardThreadLookup(notification, threadOrderLookup);
  }
  // Ambiguity withholds a conversation attachment, not the dashboard action.
  // Legacy orphan records without a row fallback remain excluded as before.
  return threadIds.length === 0 || (notification?.rowNumber ?? notification?.meta?.rowNumber) != null;
}

export function getSendableActionNotifications(notifications = []) {
  return notifications.filter(isSendableActionNotification);
}

export function getActionReasonPriority(reason = '') {
  if (reason === 'new_property_pending_approval') return 90;
  if (reason === 'tour_requested') return 80;
  if (reason === 'tour_reschedule_requested') return 79;
  if (reason === 'tour_slot_declined') return 78;
  if (reason === 'tour_unavailable') return 77;
  if (reason === 'call_requested') return 75;
  if (reason === 'reply_review_required') return 74;
  if (reason.startsWith('wrong_contact:')) return 70;
  if (reason === 'needs_user_input:unclear') return 59;
  if (reason.startsWith('needs_user_input:')) return 60;
  return 10;
}

function timestampToMillis(value) {
  if (!value) return 0;
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  }
  if (typeof value.toMillis === 'function') {
    const millis = value.toMillis();
    return Number.isFinite(millis) ? millis : 0;
  }
  if (typeof value.toDate === 'function') {
    const millis = value.toDate()?.getTime?.();
    return Number.isFinite(millis) ? millis : 0;
  }
  if (typeof value.seconds === 'number') {
    const nanos = typeof value.nanoseconds === 'number' ? value.nanoseconds : 0;
    return (value.seconds * 1000) + Math.floor(nanos / 1000000);
  }
  if (value instanceof Date) {
    const millis = value.getTime();
    return Number.isFinite(millis) ? millis : 0;
  }
  return 0;
}

export function getActionNotificationTimestamp(notification) {
  return Math.max(
    timestampToMillis(notification?.createdAt),
    timestampToMillis(notification?.updatedAt),
    timestampToMillis(notification?.handledAt),
    timestampToMillis(notification?.meta?.createdAt),
    timestampToMillis(notification?.meta?.updatedAt)
  );
}

export function compareCurrentActionNotifications(a, b) {
  const timestampDiff = getActionNotificationTimestamp(b) - getActionNotificationTimestamp(a);
  if (timestampDiff !== 0) return timestampDiff;

  const priorityDiff = getActionReasonPriority(b?.meta?.reason) - getActionReasonPriority(a?.meta?.reason);
  if (priorityDiff !== 0) return priorityDiff;

  return String(a?.id || '').localeCompare(String(b?.id || ''));
}

function normalizeSourceIdentity(value) {
  return String(value || '').trim();
}

function normalizedIdentitySet(values) {
  return new Set(values.map(normalizeSourceIdentity).filter(Boolean));
}

function identitySetsOverlap(a, b) {
  return [...a].some(value => b.has(value));
}

function actionNotificationsShareScope(a, b) {
  const aThreadIds = normalizedIdentitySet(getNotificationThreadIds(a));
  const bThreadIds = normalizedIdentitySet(getNotificationThreadIds(b));
  return aThreadIds.size > 0 && bThreadIds.size > 0 && identitySetsOverlap(aThreadIds, bThreadIds);
}

export function actionNotificationsShareSourceMessage(a, b) {
  const aMeta = a?.meta || {};
  const bMeta = b?.meta || {};
  const aSourceIds = normalizedIdentitySet([
    aMeta.sourceMessageId,
    aMeta.sourceInternetMessageId
  ]);
  const bSourceIds = normalizedIdentitySet([
    bMeta.sourceMessageId,
    bMeta.sourceInternetMessageId
  ]);

  return actionNotificationsShareScope(a, b) && identitySetsOverlap(aSourceIds, bSourceIds);
}

export function selectPreferredCurrentActionNotification(notifications = []) {
  const newestFirst = [...notifications].sort(compareCurrentActionNotifications);
  const newestAction = newestFirst[0] || null;
  if (!newestAction) return null;

  const newestSourceSiblings = newestFirst.filter(notification => (
    notification === newestAction || actionNotificationsShareSourceMessage(notification, newestAction)
  ));

  return newestSourceSiblings.sort((a, b) => {
    const priorityDiff = getActionReasonPriority(b?.meta?.reason) - getActionReasonPriority(a?.meta?.reason);
    if (priorityDiff !== 0) return priorityDiff;
    return compareCurrentActionNotifications(a, b);
  })[0] || newestAction;
}

export function selectCurrentDashboardActionNotification(notifications = [], threadOrderLookup) {
  return selectPreferredCurrentActionNotification(
    notifications.filter(notif => isRenderableDashboardActionNotification(notif, threadOrderLookup))
  );
}
